import { useState, useCallback } from "react";
import { useApi } from "./useApi";
import { newIdempotencyKey } from "../lib/onlinePayment";

/**
 * Hook do pagamento online (rota unificada /online-payments).
 * O provedor (hoje InfinitePay) é resolvido pelo backend a partir da integração
 * ativa da empresa do token; o valor é calculado no servidor a partir do pedido.
 *
 * Todas as respostas são JSON direto (sem envelope):
 * - GET  /online-payments/availability        → { available, provider?, providerName? }
 * - POST /online-payments/checkout             → OnlinePaymentDTO
 * - GET  /online-payments/{paymentId}          → OnlinePaymentDTO
 * - POST /online-payments/{paymentId}/verify   → OnlinePaymentDTO
 *
 * OnlinePaymentDTO: { paymentId, orderCode, provider, providerName, status,
 *   checkoutUrl (só em PENDING), amount, currency, message, receiptUrl,
 *   captureMethod, installments, createdAt, confirmedAt }
 */
export const useOnlinePayment = () => {
  const [availability, setAvailability] = useState({
    available: false,
    provider: null,
    providerName: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const api = useApi();

  /** Consulta se a empresa tem pagamento online ativo. Falha = indisponível. */
  const checkAvailability = useCallback(async () => {
    try {
      const data = await api.get("/online-payments/availability");
      const normalized = {
        available: data?.available === true,
        provider: data?.provider || null,
        providerName: data?.providerName || null,
      };
      setAvailability(normalized);
      return normalized;
    } catch (err) {
      console.error("❌ Erro ao consultar pagamento online:", err);
      const fallback = { available: false, provider: null, providerName: null };
      setAvailability(fallback);
      return fallback;
    }
  }, [api]);

  /**
   * Cria (ou recupera) o pagamento online do pedido.
   * @param {string} orderCode - Código do pedido (data[0].code do POST /orders)
   * @param {Object} [opts]
   * @param {boolean} [opts.newAttempt=false] - true só após UNKNOWN/CREATION_FAILED, com confirmação do cliente
   * @param {string} [opts.idempotencyKey] - Gerada automaticamente se não informada
   */
  const startCheckout = useCallback(
    async (orderCode, { newAttempt = false, idempotencyKey } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.post(
          "/online-payments/checkout",
          { orderCode: String(orderCode), newAttempt },
          {
            headers: {
              "Idempotency-Key": idempotencyKey || newIdempotencyKey(),
            },
          }
        );
        console.log("📡 Pagamento online:", data?.status);
        return data;
      } catch (err) {
        console.error("❌ Erro ao iniciar pagamento online:", err);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /** Status interno da tentativa (usado no polling da página de retorno). */
  const getPayment = useCallback(
    async (paymentId) => api.get(`/online-payments/${paymentId}`),
    [api]
  );

  /**
   * Envia os parâmetros recebidos no retorno do provedor. O backend consulta o
   * provedor antes de confirmar (os parâmetros sozinhos não confirmam nada).
   * Limite do back: 1 consulta a cada 3 s por tentativa.
   */
  const verifyReturn = useCallback(
    async (paymentId, providerParams = {}) => {
      // Remove parâmetros vazios
      const body = Object.fromEntries(
        Object.entries(providerParams).filter(([, v]) => v)
      );
      return api.post(`/online-payments/${paymentId}/verify`, body);
    },
    [api]
  );

  return {
    availability,
    loading,
    error,
    checkAvailability,
    startCheckout,
    getPayment,
    verifyReturn,
  };
};
